import {useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'

// Singleton promise to prevent multiple script loads
let bvScriptLoadingPromise = null

// Return Bazaarvoice script URL
const getBvScriptUrl = (clientName, siteId, environment, locale) =>
    `https://apps.bazaarvoice.com/deployments/${clientName}/${siteId}/${environment}/${locale}/bv.js`

// Check if the Bazaarvoice script is already in the DOM
const isBvScriptAlreadyLoaded = (clientName, siteId) => {
    return Boolean(document.querySelector(`script[src*="${clientName}/${siteId}"]`))
}

// Function to dynamically load the Bazaarvoice script
const loadBvScript = (clientName, siteId, environment, locale) => {
    if (bvScriptLoadingPromise) return bvScriptLoadingPromise

    bvScriptLoadingPromise = new Promise((resolve, reject) => {
        if (isBvScriptAlreadyLoaded(clientName, siteId)) {
            resolve()
            return
        }

        const script = document.createElement('script')
        script.src = getBvScriptUrl(clientName, siteId, environment, locale)
        script.async = true
        script.onload = resolve
        script.onerror = () => reject(new Error('Failed to load Bazaarvoice script.'))
        document.head.appendChild(script)
    })

    return bvScriptLoadingPromise
}

// Hook to manage Bazaarvoice script loading
export const useBazaarvoice = (clientName, siteId, environment, provider, locale) => {
    const isInitialized = useRef(false)
    useEffect(() => {
        if (provider !== 'bazaarvoice') return
        if (!isInitialized.current) {
            loadBvScript(clientName, siteId, environment, locale).catch(console.error)
            isInitialized.current = true
        }
    }, [clientName, siteId, environment, locale])

    const api = useMemo(
        () => ({
            loadBazaarvoice: () => loadBvScript(clientName, siteId, environment, locale),
        }),
        [clientName, siteId, environment, locale],
    )

    return api
}

useBazaarvoice.propTypes = {
    clientName: PropTypes.string.isRequired,
    siteId: PropTypes.string.isRequired,
    environment: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    provider: PropTypes.string.isRequired,
}
